import { useCallback } from 'react';
import { useNavigate, useSearchParams, type NavigateOptions } from 'react-router-dom';

/**
 * Like ``useNavigate``, but threads the current ``?chat=`` query
 * through the destination URL so navigating to an artifact-detail
 * route keeps the chat sidecar mounted.
 *
 * Scope routes are path-based; chat is sidecar sticky context —
 * see OR-5963 URL refactor. Call sites that move the user INTO an
 * artifact-detail route from the project-page surfaces should
 * thread ``?chat=`` through so a user clicking from the artifact
 * list into a cohort while a chat is open doesn't lose the chat.
 *
 * Doesn't replace ``useNavigate`` everywhere — only the project-
 * page-level navigates that move between scope routes. Navigation
 * inside detail pages (cohort builder, AV editor, etc.) stays on
 * the plain ``useNavigate`` since those surfaces already preserve
 * the search params naturally via their internal routing.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const useChatPreservingNavigate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return useCallback(
    (pathname: string, options?: NavigateOptions) => {
      const chat = searchParams.get('chat');
      const search = chat ? `?chat=${chat}` : '';
      // Pass options only when defined — calling navigate(arg, undefined)
      // leaks a second argument that test mocks see as a real call shape,
      // and the production navigate signature treats omission and
      // undefined identically.
      if (options) {
        navigate({ pathname, search }, options);
      } else {
        navigate({ pathname, search });
      }
    },
    [navigate, searchParams]
  );
};
